import { useState, useCallback, useContext } from "react";
import { Dropdown } from "~/components/UI";
import MerchantsContext from "./merchantsContext";
import { useTranslation } from "react-i18next";
import { SOURCE_TYPE } from "~/api/common";
import useCreateIncidentDialog from "~/components/Incidents/CreateIncident/useCreateIncidentDialog";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

interface MerchantOptionsProps {
  merchant: MerchantsApi.MerchantDto;
}

/**
 * @memberof Merchants
 * @component
 * @desc Merchant options. Using a MerchantsContext.
 * @property {MerchantsApi.MerchantDto} merchant - merchant data;
 * @requires MerchantsContext
 */

const MerchantOptions = ({ merchant }: MerchantOptionsProps) => {
  const [loading, setLoading] = useState(false);
  const { onEdit, onUpdate, onUpdateItem, onDelete } =
    useContext(MerchantsContext);
  const [createIncident] = useCreateIncidentDialog(SOURCE_TYPE.MERCHANT);
  const { enabled } = merchant;
  const { hasAll, hasAny } = usePermissions();
  const { t } = useTranslation();

  const onUpdateHandler = useCallback((res: Promise<unknown> | void) => {
    if (res) {
      setLoading(true);
      res.finally(() => {
        setLoading(false);
      });
    }
  }, []);

  const onCreatedIncident = useCallback(
    (incident: IncidentsApi.IncidentDto) => {
      if (merchant.incidents) {
        merchant.incidents.count++;
        merchant.incidents.incidentIds.push(incident.incidentId);
      } else {
        merchant.incidents = {
          count: 1,
          incidentIds: [incident.incidentId],
        };
      }
      onUpdateItem && onUpdateItem(merchant);
    },
    [merchant, onUpdateItem]
  );

  if (
    !hasAny([
      PERMISSION.EDIT_MERCHANT,
      PERMISSION.DELETE_MERCHANT,
      PERMISSION.CREATE_JOURNAL_INCIDENTS,
    ])
  ) {
    return null;
  }

  const options = [
    {
      label: enabled ? t("option.disable") : t("option.enable"),
      hidden: !hasAll(PERMISSION.EDIT_MERCHANT),
      confirmation: {
        cancelText: t("button.no"),
        okText: t("button.yes"),
        title: t("text.confirmationRequest"),
        onConfirm: () =>
          onUpdate &&
          onUpdateHandler(onUpdate(merchant.id, { enabled: !enabled })),
      },
    },
    {
      label: t("option.edit"),
      hidden: !hasAll(PERMISSION.EDIT_MERCHANT),
      onClick: () => onEdit && onEdit(merchant),
    },
    {
      label: t("option.delete"),
      hidden: !hasAll(PERMISSION.DELETE_MERCHANT),
      onClick: () => onDelete && onUpdateHandler(onDelete(merchant)),
    },
    {
      label: t("option.createIncident"),
      hidden: !hasAll(PERMISSION.CREATE_JOURNAL_INCIDENTS),
      onClick: () => createIncident(merchant.id, onCreatedIncident),
    },
  ];

  return <Dropdown loading={loading} options={options} />;
};

export default MerchantOptions;
